import { useCallback, useEffect, useState } from "react";
import { Loader2, Landmark, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/AppLayout";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, usePoll } from "@/lib/api";
import { toast } from "sonner";

const TYPE_CLS = { loan: "bg-blue-50 text-blue-700 border-blue-200", cdfi: "bg-emerald-50 text-emerald-700 border-emerald-200", crowdfunding: "bg-purple-50 text-purple-700 border-purple-200", matching: "bg-amber-50 text-amber-700 border-amber-200" };

export default function Capital() {
  const { hasPkg } = useAuth();
  const allowed = hasPkg("licensing_guide");
  const [d, setD] = useState(null);
  const [need, setNeed] = useState("");
  const load = useCallback(() => api.get("/capital").then((r) => { setD(r.data); setNeed((n) => n || r.data.need); }), []);
  useEffect(() => { load(); }, [load]);
  usePoll(load, d?.status === "pending", 3000);
  const run = async () => {
    try { await api.post("/capital", { need }); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Area 02 · Capital Matchmaker" title="Alternative capital">Don't qualify for a grant, or need matching funds? See SBA microloans, local CDFIs, crowdfunding and more that fit your business.</PageHeader>
      {!allowed ? <UpgradeGate pkg="licensing_guide" title="Unlock the Capital Matchmaker">Vetted alternative and matching-fund options tailored to your business profile and location.</UpgradeGate> : !d ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col md:flex-row gap-3" data-testid="capital-form">
            <Textarea rows={2} placeholder="What do you need? e.g. $40,000 to buy equipment, or a 25% match for a state grant" value={need} onChange={(e) => setNeed(e.target.value)} data-testid="capital-need-input" />
            <Button onClick={run} disabled={d.status === "pending"} className="bg-emerald-600 hover:bg-emerald-700 md:self-end shrink-0" data-testid="capital-submit-button">{d.status === "pending" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Find capital options</Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Uses your Company profile (state, city, industry, entity type). AI-researched — confirm terms with each provider.</p>
          {d.status === "pending" && <p className="mt-6 text-sm text-emerald-700" data-testid="capital-pending">Matching capital options to your business…</p>}
          {d.status === "error" && <p className="mt-6 text-sm text-red-600">Something went wrong. Try again.</p>}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="capital-results">
            {(d.options || []).map((o, i) => (
              <div key={i} className="card-lift bg-white border border-slate-200 rounded-lg p-5" data-testid={`capital-option-${i}`}>
                <div className="flex items-center gap-2"><Landmark className="h-4 w-4 text-emerald-600" /><span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${TYPE_CLS[o.type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{o.type}</span></div>
                <h3 className="font-semibold text-slate-900 mt-3">{o.name}</h3>
                <p className="text-xs text-slate-500">{o.provider}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <div><span className="block font-mono text-[10px] uppercase text-slate-400">Amount</span>{o.typical_amount}</div>
                  <div><span className="block font-mono text-[10px] uppercase text-slate-400">Terms</span>{o.typical_terms}</div>
                </div>
                <p className="text-sm text-slate-600 mt-3">{o.best_for}</p>
                {o.url && <a href={o.url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 font-semibold mt-3 inline-flex items-center gap-1 hover:underline"><ExternalLink className="h-3 w-3" />Official site</a>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
